// Chakra imports
import {
  Box,
  Flex,
  Grid,
  Input,
  Select,
  Button,
  Text,
  Icon,
  FormControl,
  FormLabel,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Collapse,
  useDisclosure,
  HStack
} from "@chakra-ui/react";
import React, { useState, useEffect, useCallback } from "react";
import {
  FaFilter,
  FaCalendarAlt,
  FaSearch,
  FaUsers,
  FaDollarSign,
  FaRedo,
  FaChevronDown,
  FaChevronUp
} from "react-icons/fa";
import { FiRefreshCw } from "react-icons/fi";

// Servicios
import { 
  saveFilters, 
  getDefaultFilters,
  formatCurrency,
  searchCustomers,
  getCustomerById
} from "services/billing";
import { getSiteConfig } from 'utils/site';

// Utilidad debounce simple
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const PaymentFilters = ({
  filters,
  onFiltersChange,
  onStatsReload,
  isLoading = false
}) => {
  // Estados locales
  const [localFilters, setLocalFilters] = useState(filters);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const { isOpen: isAdvancedOpen, onToggle: onAdvancedToggle } = useDisclosure();

  // Colores del tema (Datofy V1 — cream/coral/ink)
  const bgColor = "white";
  const borderColor = "cream.line";
  const textColor = "ink.900";
  const placeholderColor = "ink.400";

  // Estilo compartido para inputs/selects
  const inputStyles = {
    bg: "cream.bg",
    borderWidth: "1.5px",
    borderColor: "cream.line2",
    borderRadius: "10px",
    color: "ink.900",
    fontWeight: "500",
    _hover: { borderColor: "ink.400" },
    _focus: { borderColor: "coral.500", boxShadow: "0 0 0 1px var(--chakra-colors-coral-500)" },
  };

  // Opciones de estado de pago
  const statusOptions = [
    { value: 'all', label: 'Todos los estados' },
    { value: 'completed', label: 'Completado' },
    { value: 'pending', label: 'Pendiente' },
    { value: 'processing', label: 'Procesando' },
    { value: 'on-hold', label: 'En espera' },
    { value: 'repactado', label: 'Repactado' },
    { value: 'cancelled', label: 'Cancelado' },
    { value: 'refunded', label: 'Reembolsado' },
    { value: 'failed', label: 'Fallido' }
  ];

  // Presets de fechas
  const datePresets = [
    { 
      label: 'Año Actual', 
      value: 'current_year',
      start_date: `${new Date().getFullYear()}-01-01`,
      end_date: `${new Date().getFullYear()}-12-31`
    },
    { 
      label: 'Último Año', 
      value: 'last_year',
      start_date: `${new Date().getFullYear() - 1}-01-01`,
      end_date: `${new Date().getFullYear() - 1}-12-31`
    },
    { 
      label: 'Último Trimestre', 
      value: 'last_quarter',
      start_date: (() => {
        const date = new Date();
        date.setMonth(date.getMonth() - 3);
        return date.toISOString().split('T')[0];
      })(),
      end_date: new Date().toISOString().split('T')[0]
    },
    { 
      label: 'Últimos 30 días', 
      value: 'last_30_days',
      start_date: (() => {
        const date = new Date();
        date.setDate(date.getDate() - 30);
        return date.toISOString().split('T')[0];
      })(),
      end_date: new Date().toISOString().split('T')[0]
    }
  ];

  // Cargar grupos disponibles
  useEffect(() => {
    loadAvailableGroups();
  }, []);

  // Sincronizar filtros SOLO en casos específicos (evitar sobrescritura)
  useEffect(() => {
    // Solo sincronizar si los filtros del padre fueron limpiados (reset completo)
    if (!filters.customer_id && !filters.start_date && !filters.end_date) {
      console.log('PaymentFilters: Resetting filters from parent');
      setLocalFilters(filters);
      setSelectedCustomer(null);
      setCustomerSearch('');
      setCustomerResults([]);
    }
  }, [filters]);

  // Recuperar datos del cliente seleccionado al cargar si hay customer_id
  useEffect(() => {
    const loadSelectedCustomer = async () => {
      if (localFilters.customer_id && !selectedCustomer) {
        console.log('PaymentFilters: Loading customer data for ID:', localFilters.customer_id);
        
        try {
          const customerData = await getCustomerById(localFilters.customer_id);
          if (customerData) {
            console.log('PaymentFilters: Customer data loaded:', customerData);
            setSelectedCustomer(customerData);
            setCustomerSearch(customerData.name || customerData.display_text || `Cliente ID: ${customerData.id}`);
          } else {
            console.warn('PaymentFilters: Customer not found for ID:', localFilters.customer_id);
            // Limpiar customer_id si el cliente no existe
            const newFilters = { ...localFilters, customer_id: '' };
            setLocalFilters(newFilters);
            saveFilters(newFilters);
          }
        } catch (error) {
          console.error('PaymentFilters: Error loading customer:', error);
        }
      }
    };

    loadSelectedCustomer();
  }, [localFilters.customer_id, selectedCustomer]);

  const loadAvailableGroups = async () => {
    try {
      setLoadingGroups(true);
      // Obtener grupos desde la configuración del sitio (mismo patrón que suscripciones)
      const config = JSON.parse(getSiteConfig());
      const loadedGroups = config?.groups || [];
      
      // Convertir a array simple de nombres
      const groupNames = loadedGroups.map(grupo => grupo.name || grupo);
      setAvailableGroups(groupNames);
    } catch (error) {
      console.error('Error loading groups from config:', error);
      setAvailableGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  };

  // Buscar clientes AJAX con debounce
  const handleCustomerSearch = useCallback(async (searchTerm) => {
    if (searchTerm.length < 2) {
      setCustomerResults([]);
      return;
    }
    
    setLoadingCustomers(true);
    try {
      const results = await searchCustomers(searchTerm, 10);
      setCustomerResults(results);
    } catch (error) {
      console.error('Error searching customers:', error);
      setCustomerResults([]);
    } finally {
      setLoadingCustomers(false);
    }
  }, []);

  // Debounce para búsqueda de clientes (500ms)
  const debouncedCustomerSearch = useCallback(
    debounce(handleCustomerSearch, 500),
    [handleCustomerSearch]
  );

  // Seleccionar cliente
  const handleCustomerSelect = (customer) => {
    console.log('PaymentFilters: Customer selected', customer);
    
    setSelectedCustomer(customer);
    setCustomerSearch(customer.name || customer.display_text);
    setCustomerResults([]);
    
    // Actualizar filtros locales INMEDIATAMENTE
    const newFilters = {
      ...localFilters,
      customer_id: customer.id
    };
    
    console.log('PaymentFilters: Setting localFilters with customer_id', newFilters);
    setLocalFilters(newFilters);
    
    // GUARDAR INMEDIATAMENTE en localStorage (nueva arquitectura)
    console.log('PaymentFilters: Saving filters immediately to localStorage');
    saveFilters(newFilters);
  };

  // Limpiar selección de cliente
  const clearCustomerSelection = () => {
    console.log('PaymentFilters: Clearing customer selection');
    
    setSelectedCustomer(null);
    setCustomerSearch('');
    setCustomerResults([]);
    
    // Actualizar filtros locales
    const newFilters = {
      ...localFilters,
      customer_id: ''
    };
    setLocalFilters(newFilters);
    
    // GUARDAR INMEDIATAMENTE en localStorage (nueva arquitectura)
    console.log('PaymentFilters: Saving cleared customer filter to localStorage');
    saveFilters(newFilters);
  };

  // Manejar cambios en filtros locales (nueva arquitectura - guarda inmediatamente)
  const handleFilterChange = (key, value) => {
    const newFilters = {
      ...localFilters,
      [key]: value
    };
    
    console.log(`PaymentFilters: Filter changed - ${key}:`, value);
    setLocalFilters(newFilters);
    
    // GUARDAR INMEDIATAMENTE en localStorage (nueva arquitectura)
    console.log('PaymentFilters: Saving filter change immediately to localStorage');
    saveFilters(newFilters);
  };

  // Aplicar preset de fecha (nueva arquitectura - guarda inmediatamente)
  const applyDatePreset = (preset) => {
    const newFilters = {
      ...localFilters,
      start_date: preset.start_date,
      end_date: preset.end_date
    };
    
    console.log('PaymentFilters: Date preset applied:', preset.label);
    setLocalFilters(newFilters);
    
    // GUARDAR INMEDIATAMENTE en localStorage (nueva arquitectura)
    console.log('PaymentFilters: Saving date preset immediately to localStorage');
    saveFilters(newFilters);
  };

  // Aplicar filtros (SOLO ejecuta búsqueda, no guarda - nueva arquitectura)
  const applyFilters = () => {
    // Construir filtros finales dinámicamente
    const finalFilters = {
      ...localFilters,
      // Asegurar que customer_id siempre refleje el cliente seleccionado actual
      customer_id: selectedCustomer?.id || localFilters.customer_id || ''
    };
    
    console.log('PaymentFilters: Applying filters (SEARCH ONLY)', {
      localFilters,
      finalFilters,
      selectedCustomer: selectedCustomer?.id,
      customerSearch,
      message: 'Filters already saved, just triggering search'
    });
    
    // NO guardar filtros aquí - ya están guardados desde el cambio individual
    // saveFilters(finalFilters); // REMOVIDO
    
    // Notificar cambio de filtros al componente padre para ejecutar búsqueda
    onFiltersChange(finalFilters);
    
    // Recargar estadísticas (si está disponible)
    if (onStatsReload && typeof onStatsReload === 'function') {
      onStatsReload();
    }
  };

  // Limpiar filtros
  const clearFilters = () => {
    console.log('PaymentFilters: Clearing all filters');
    
    const defaultFilters = getDefaultFilters();
    
    // Limpiar selección de cliente ANTES de aplicar filtros
    setSelectedCustomer(null);
    setCustomerSearch('');
    setCustomerResults([]);
    
    // Actualizar filtros locales
    setLocalFilters(defaultFilters);
    
    // Guardar filtros por defecto en localStorage
    saveFilters(defaultFilters);
    
    // Notificar cambio al componente padre
    onFiltersChange(defaultFilters);
    
    // Recargar estadísticas (si está disponible)
    if (onStatsReload && typeof onStatsReload === 'function') {
      onStatsReload();
    }
  };

  // Contar filtros activos
  const getActiveFiltersCount = () => {
    let count = 0;
    if (localFilters.customer_id) count++;
    if (localFilters.order_id) count++;
    if (localFilters.status !== 'all') count++;
    if (localFilters.group) count++;
    if (localFilters.min_amount) count++;
    if (localFilters.max_amount) count++;
    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <Box
      bg={bgColor}
      p={{ base: 4, md: 6 }}
      borderRadius="16px"
      border="1px solid"
      borderColor={borderColor}
      boxShadow="0px 3.5px 5.5px rgba(0,0,0,0.02)"
      maxW="100%"
      overflowX="hidden"
      fontFamily="jakarta"
    >
      {/* Encabezado */}
      <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={2}>
        <HStack>
          <Icon as={FaFilter} color="coral.500" />
          <Text fontWeight="800" color={textColor} letterSpacing="-0.01em">
            Filtros de Búsqueda
          </Text>
          {activeFiltersCount > 0 && (
            <Box as="span" px="8px" py="2px" borderRadius="999px" fontSize="11px" fontWeight="700" bg="coral.soft" color="coral.700">
              {activeFiltersCount}
            </Box>
          )}
        </HStack>

        <HStack spacing={2} flexWrap="wrap">
          <Button
            size="sm"
            bg="cream.bg2"
            color="ink.700"
            borderRadius="10px"
            _hover={{ bg: 'coral.soft', color: 'coral.700' }}
            onClick={onAdvancedToggle}
            rightIcon={<Icon as={isAdvancedOpen ? FaChevronUp : FaChevronDown} />}
          >
            Avanzado
          </Button>
          <Button
            size="sm"
            bg="transparent"
            border="1px solid"
            borderColor="cream.line2"
            color="ink.700"
            borderRadius="10px"
            _hover={{ bg: 'cream.bg2' }}
            onClick={clearFilters}
            leftIcon={<Icon as={FaRedo} />}
          >
            Limpiar
          </Button>
          <Button
            size="sm"
            bg="coral.500"
            color="white"
            fontWeight="600"
            borderRadius="10px"
            _hover={{ bg: 'coral.600' }}
            _active={{ bg: 'coral.700' }}
            onClick={applyFilters}
            leftIcon={<Icon as={FaFilter} />}
            isLoading={isLoading}
            loadingText="Buscando..."
          >
            Aplicar
          </Button>
        </HStack>
      </Flex>

      {/* Filtros Básicos */}
      <Grid templateColumns={{ base: "1fr", md: "2fr 1fr 1fr 1fr" }} gap={4} mb={4}>
        {/* Rango de Fechas */}
        <FormControl>
          <FormLabel fontSize="sm" color="ink.700" fontWeight="600">
            <Icon as={FaCalendarAlt} mr={2} />
            Período
          </FormLabel>
          <Grid templateColumns="1fr 1fr" gap={2}>
            <Input
              type="date"
              size="sm"
              value={localFilters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
              {...inputStyles}
            />
            <Input
              type="date"
              size="sm"
              value={localFilters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
              {...inputStyles}
            />
          </Grid>
          {/* Presets de fecha */}
          <HStack mt={2} spacing={1} flexWrap="wrap">
            {datePresets.map((preset) => (
              <Button
                key={preset.value}
                size="xs"
                bg="cream.bg2"
                color="ink.700"
                borderRadius="8px"
                onClick={() => applyDatePreset(preset)}
                _hover={{ bg: "coral.soft", color: "coral.700" }}
              >
                {preset.label}
              </Button>
            ))}
          </HStack>
        </FormControl>

        {/* Estado */}
        <FormControl>
          <FormLabel fontSize="sm" color="ink.700" fontWeight="600">Estado</FormLabel>
          <Select
            size="sm"
            value={localFilters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            {...inputStyles}
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </FormControl>


        {/* Búsqueda de Cliente AJAX */}
        <FormControl position="relative">
          <FormLabel fontSize="sm" color="ink.700" fontWeight="600">
            <Icon as={FaSearch} mr={2} />
            Buscar Cliente
          </FormLabel>
          <InputGroup size="sm">
            <InputLeftElement>
              <Icon as={FaSearch} color={placeholderColor} />
            </InputLeftElement>
            <Input
              placeholder="Buscar por nombre o email..."
              value={customerSearch}
              onChange={(e) => {
                const value = e.target.value;
                setCustomerSearch(value);

                // Solo buscar si no hay cliente seleccionado o si el valor difiere
                if (!selectedCustomer || value !== selectedCustomer.name) {
                  debouncedCustomerSearch(value);
                }
              }}
              {...inputStyles}
            />
            {selectedCustomer && (
              <InputRightElement>
                <Button
                  size="xs"
                  variant="ghost"
                  color="ink.400"
                  _hover={{ color: 'coral.700', bg: 'coral.soft' }}
                  onClick={clearCustomerSelection}
                >
                  ×
                </Button>
              </InputRightElement>
            )}
          </InputGroup>

          {/* Resultados de búsqueda */}
          {customerResults.length > 0 && (
            <Box
              position="absolute"
              top="100%"
              left={0}
              right={0}
              bg="white"
              border="1px solid"
              borderColor="cream.line"
              borderTop="none"
              borderRadius="0 0 10px 10px"
              maxH="200px"
              overflowY="auto"
              className="thin-scrollbar"
              zIndex={10}
              boxShadow="0px 6px 12px rgba(43,39,35,0.08)"
            >
              {customerResults.map((customer) => (
                <Box
                  key={customer.id}
                  p={2}
                  cursor="pointer"
                  _hover={{ bg: "coral.tint" }}
                  onClick={() => handleCustomerSelect(customer)}
                  borderBottom="1px solid"
                  borderColor="cream.line"
                >
                  <Text fontSize="sm" fontWeight="600" color="ink.900">
                    {customer.name}
                  </Text>
                  <Text fontSize="xs" color="ink.400">
                    {customer.email}
                  </Text>
                </Box>
              ))}
            </Box>
          )}

          {loadingCustomers && customerSearch.length >= 2 && (
            <Box
              position="absolute"
              top="100%"
              left={0}
              right={0}
              bg="white"
              border="1px solid"
              borderColor="cream.line"
              borderTop="none"
              borderRadius="0 0 10px 10px"
              textAlign="center"
              p={2}
              zIndex={10}
              boxShadow="0px 6px 12px rgba(43,39,35,0.08)"
            >
              <Text fontSize="xs" color="ink.400">Buscando...</Text>
            </Box>
          )}
        </FormControl>
      </Grid>

      {/* Filtros Avanzados */}
      <Collapse in={isAdvancedOpen} animateOpacity>
        <Box pt={4} borderTop="1px solid" borderColor="cream.line">
          <Grid templateColumns={{ base: "1fr", md: "1fr 1fr 1fr" }} gap={4}>
            {/* Grupo/Curso */}
            <FormControl>
              <FormLabel fontSize="sm" color="ink.700" fontWeight="600">
                <Icon as={FaUsers} mr={2} />
                Grupo/Curso
              </FormLabel>
              <Select
                size="sm"
                value={localFilters.group}
                onChange={(e) => handleFilterChange('group', e.target.value)}
                {...inputStyles}
                isDisabled={loadingGroups}
              >
                <option value="">Todos los grupos</option>
                {availableGroups.map((group) => (
                  <option key={group} value={group}>
                    {group}
                  </option>
                ))}
              </Select>
            </FormControl>

            {/* Monto Mínimo */}
            <FormControl>
              <FormLabel fontSize="sm" color="ink.700" fontWeight="600">
                <Icon as={FaDollarSign} mr={2} />
                Monto Mínimo
              </FormLabel>
              <InputGroup size="sm">
                <InputLeftElement>
                  <Text fontSize="sm" color={placeholderColor}>$</Text>
                </InputLeftElement>
                <Input
                  type="number"
                  placeholder="0"
                  value={localFilters.min_amount}
                  onChange={(e) => handleFilterChange('min_amount', e.target.value)}
                  {...inputStyles}
                />
              </InputGroup>
            </FormControl>

            {/* Monto Máximo */}
            <FormControl>
              <FormLabel fontSize="sm" color="ink.700" fontWeight="600">
                <Icon as={FaDollarSign} mr={2} />
                Monto Máximo
              </FormLabel>
              <InputGroup size="sm">
                <InputLeftElement>
                  <Text fontSize="sm" color={placeholderColor}>$</Text>
                </InputLeftElement>
                <Input
                  type="number"
                  placeholder="999999999"
                  value={localFilters.max_amount}
                  onChange={(e) => handleFilterChange('max_amount', e.target.value)}
                  {...inputStyles}
                />
              </InputGroup>
            </FormControl>
          </Grid>

          {/* Información de rango de montos */}
          {(localFilters.min_amount || localFilters.max_amount) && (
            <Box mt={2} p={2} bg="sky.bg" borderRadius="8px">
              <Text fontSize="xs" color="sky.ink" fontWeight="600">
                Rango de montos: {
                  localFilters.min_amount ?
                    formatCurrency(localFilters.min_amount) :
                    '$0'
                } - {
                  localFilters.max_amount ?
                    formatCurrency(localFilters.max_amount) :
                    '∞'
                }
              </Text>
            </Box>
          )}
        </Box>
      </Collapse>

      {/* Información de filtros aplicados */}
      {activeFiltersCount > 0 && (
        <Box mt={4} p={3} bg="coral.tint" borderRadius="10px" borderLeft="4px solid" borderColor="coral.500">
          <Text fontSize="sm" color="coral.700" fontWeight="700">
            <Icon as={FaFilter} mr={2} />
            {activeFiltersCount} filtro{activeFiltersCount > 1 ? 's' : ''} activo{activeFiltersCount > 1 ? 's' : ''}
          </Text>
          <Text fontSize="xs" color="ink.700" mt={1}>
            Los resultados se actualizarán al hacer clic en "Aplicar"
          </Text>

          {/* Información especial para búsqueda */}
          {selectedCustomer && (
            <Text fontSize="xs" color="ink.700" mt={1} fontStyle="italic">
              💡 Filtrando por cliente: {selectedCustomer.name}
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PaymentFilters;